import { useQuery, UseQueryOptions } from '@tanstack/react-query'
import { APIList } from 'src/interfaces'
import { Entities } from 'src/enums'
import { Product } from 'src/api'
import { useApiInstance } from './useApiInstance'
import {
  buildRequestParams,
  ObjectType,
  transformPage,
  transformPerPage,
  transformSearch,
} from './helpers'

interface RequestParams {
  params?: Partial<AdminProductsGetParams>;
  paramsTransform?: Array<(params: Record<string, any>) => Record<string, any>>;
  options?: Omit<
    UseQueryOptions<unknown, Error, APIList<Product>>,
    'queryKey' | 'queryFn'
  >;
}

interface AdminProductsGetParams {
  limit: number;
  offset: number;
  sort: string;
  manufacturerId: number;
  typeId: number;
  name: string;
  projectId: number;
  noShelfLife: boolean;
  noRanges: boolean;
  noTips: boolean;
  inUse: boolean;
}

const schema: ObjectType['schema'] = {
  limit: 'number',
  offset: 'number',
  sort: 'string',
  manufacturerId: 'number',
  typeId: 'number',
  name: 'string',
  projectId: 'number',
  noShelfLife: 'boolean',
  noRanges: 'boolean',
  noTips: 'boolean',
  inUse: 'boolean',
}

export const useProductListRequest = ({
  params = {},
  paramsTransform = [],
  options,
}: RequestParams = {}) => {
  const api = useApiInstance()

  const transformedParams = [
    transformPerPage,
    transformPage,
    transformSearch('name'),
    ...paramsTransform,
  ].reduce((param, transform) => transform(param), params)

  const queryParams = buildRequestParams<AdminProductsGetParams>(
    transformedParams,
    schema,
  )

  return useQuery<unknown, Error, APIList<Product>>(
    [Entities.Products, JSON.stringify(transformedParams)],
    async () => {
      const {
        limit,
        offset,
        sort,
        manufacturerId,
        typeId,
        name,
        projectId,
        noShelfLife,
        noRanges,
        noTips,
        inUse,
      } = queryParams
      const { data } = await api.v1AdminProductsGet(
        limit,
        offset,
        sort,
        manufacturerId,
        typeId,
        name,
        projectId,
        noShelfLife,
        noRanges,
        noTips,
        inUse,
      )
      return data
    },
    {
      enabled: Object.keys(transformedParams).length > 0,
      ...options,
    },
  )
}
